// Zod DTOs for the `/v1/levels` authoring surface (spec §3.3 `Level` model,
// §3.5, §6.2) — single source of truth for `apps/api` validators + the
// OpenAPI generator. `examBoardId` is a path param (`POST
// /v1/exam-boards/:id/levels`), never part of the body.
import { z } from 'zod';
import { TabKind } from '../enums';
import {
  codeSchema,
  descriptionSchema,
  reorderRequestSchema,
  slugSchema,
  titleSchema,
} from './primitives';

const tabKindEnumSchema = z.enum(Object.values(TabKind) as [TabKind, ...TabKind[]]);

// ─── POST /v1/exam-boards/:id/levels ────────────────────────────────────────

export const levelCreateRequestSchema = z.object({
  code: codeSchema,
  name: titleSchema,
  slug: slugSchema.optional(),
  description: descriptionSchema,
});
export type LevelCreateRequest = z.infer<typeof levelCreateRequestSchema>;

// ─── PATCH /v1/levels/:id ────────────────────────────────────────────────────

export const levelUpdateRequestSchema = levelCreateRequestSchema.partial();
export type LevelUpdateRequest = z.infer<typeof levelUpdateRequestSchema>;

// ─── POST /v1/levels/reorder (scoped to one board) ──────────────────────────

export const levelReorderRequestSchema = reorderRequestSchema;
export type LevelReorderRequest = z.infer<typeof levelReorderRequestSchema>;

// ─── PATCH /v1/levels/:id/structure ─────────────────────────────────────────
// `sectionLabels` — "depth -> noun" (spec §3.3 comment: `{"0":"Chapter"}` or
// `{"0":"Unit","1":"Topic"}`). `Section.depth` is enforced <= 2 (spec §6.2
// "Tree mutation semantics", `422 SECTION_DEPTH_EXCEEDED`), so only depths
// "0"/"1"/"2" are valid keys — modelled as an explicit `.strict()` object
// (not `z.record`) so an unknown depth key is a schema-level rejection, not
// a silently-accepted extra property.

export const sectionLabelsSchema = z
  .object({
    '0': z.string().trim().min(1).max(40).optional(),
    '1': z.string().trim().min(1).max(40).optional(),
    '2': z.string().trim().min(1).max(40).optional(),
  })
  .strict();
export type SectionLabels = z.infer<typeof sectionLabelsSchema>;

/** `defaultTabs` — the `ResourceTab` kinds auto-created on each new section
 * in this level (spec §3.3/§3.5). Order is meaningful (it seeds each new
 * section's tab `position` sequence), so this is validated as an array, not
 * a set — but duplicate kinds would collide with `ResourceTab`'s
 * `@@unique([sectionId, kind, label])`, so duplicates are rejected here. */
export const defaultTabsSchema = z
  .array(tabKindEnumSchema)
  .max(Object.values(TabKind).length)
  .superRefine((tabs, ctx) => {
    const seen = new Set<string>();
    for (const [index, tab] of tabs.entries()) {
      if (seen.has(tab)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Duplicate tab kind "${tab}" in defaultTabs`,
          path: [index],
        });
      }
      seen.add(tab);
    }
  });
export type DefaultTabs = z.infer<typeof defaultTabsSchema>;

export const levelStructureRequestSchema = z.object({
  sectionLabels: sectionLabelsSchema,
  defaultTabs: defaultTabsSchema,
});
export type LevelStructureRequest = z.infer<typeof levelStructureRequestSchema>;
